import type {
  ChangeReasonCode,
  ChangeReasonInput
} from '@/Network/Types/Requests/ChangeReasonRequests'

export interface ChangeReasonOption {
  value: ChangeReasonCode
  title: string
  requiresText?: boolean
}

/** Estado do campo no modal (o `v-model` do `ChangeReasonField`). */
export interface ChangeReasonValue {
  code: ChangeReasonCode | null
  note: string
}

export type ChangeReasonError =
  | 'CODE_REQUIRED'
  | 'NOTE_REQUIRED'
  | 'NOTE_TOO_SHORT'
  | 'NOTE_TOO_LONG'

export const CHANGE_REASON_NOTE_MIN = 20
export const CHANGE_REASON_NOTE_MAX = 500
export const EARLY_WITHDRAWAL_MIN_MS = 30 * 24 * 60 * 60 * 1000

export const CHANGE_REASON_HELP_TEXT =
  'Obrigatório. O motivo fica registrado no histórico do plantão. O médico não vê este motivo.'

export const CHANGE_REASON_ERROR_MESSAGES: Record<ChangeReasonError, string> = {
  CODE_REQUIRED: 'Escolha o motivo para continuar',
  NOTE_REQUIRED: 'A descrição é obrigatória para este motivo',
  NOTE_TOO_SHORT: `Escreva pelo menos ${CHANGE_REASON_NOTE_MIN} caracteres`,
  NOTE_TOO_LONG: `Use no máximo ${CHANGE_REASON_NOTE_MAX} caracteres`
}

const OTHER: ChangeReasonOption = { value: 'OTHER', title: 'Outro', requiresText: true }
const INTERNAL_FAILURE: ChangeReasonOption = { value: 'INTERNAL_FAILURE', title: 'Falha interna' }
const CLOSED_BY_CONTRACTOR: ChangeReasonOption = {
  value: 'CLOSED_BY_CONTRACTOR',
  title: 'Vaga fechada pela contratante'
}
const CLOSED_BY_COMPETITOR: ChangeReasonOption = {
  value: 'CLOSED_BY_COMPETITOR',
  title: 'Vaga fechada pela concorrente'
}
const AGENDA_REALLOCATION: ChangeReasonOption = {
  value: 'AGENDA_REALLOCATION',
  title: 'Remanejamento de agenda'
}

export const REMOVAL_REASON_OPTIONS: ChangeReasonOption[] = [
  { value: 'CLT_LEAVE', title: 'Atestado/Afastamento médico CLT' },
  { value: 'OWN_LEAVE', title: 'Atestado/Afastamento médico nosso' },
  { value: 'FORCE_MAJEURE', title: 'Força maior ou justo impedimento' },
  { value: 'MANAGEMENT_RELEASE', title: 'Liberação da coordenação/gerência', requiresText: true },
  {
    value: 'EARLY_WITHDRAWAL',
    title: 'Desistência do plantão com mais de 30 dias de antecedência'
  },
  INTERNAL_FAILURE,
  { value: 'NO_SHOW', title: 'Falta médica' },
  { value: 'OFF_APP_SWAP', title: 'Troca fora do aplicativo' },
  {
    value: 'AGENDA_RESCHEDULING',
    title: 'Reagendamento de agenda / Remanejamento da vaga',
    requiresText: true
  },
  {
    value: 'AGENDA_CANCELLATION',
    title: 'Cancelamento de agenda / Cancelamento da vaga',
    requiresText: true
  },
  OTHER
]

export const EXTERNAL_REASON_OPTIONS: ChangeReasonOption[] = [
  { value: 'REGULAR_EXTERNAL', title: 'Vaga normal do médico externo' },
  CLOSED_BY_CONTRACTOR,
  CLOSED_BY_COMPETITOR,
  INTERNAL_FAILURE,
  OTHER
]

export const OPENING_REASON_OPTIONS: ChangeReasonOption[] = [
  { value: 'REGULAR_REQUEST', title: 'Solicitação normal da unidade' },
  { value: 'CLT_LEAVE', title: 'Atestado/Afastamento de CLT' },
  { value: 'EXTRA_REQUEST', title: 'Vaga extra solicitada pela unidade' },
  INTERNAL_FAILURE,
  AGENDA_REALLOCATION,
  { value: 'SIZING_INCREASE', title: 'Aumento no redimensionamento' },
  OTHER
]

export const DELETION_REASON_OPTIONS: ChangeReasonOption[] = [
  INTERNAL_FAILURE,
  { value: 'SIZING_DECREASE', title: 'Diminuição no redimensionamento' },
  AGENDA_REALLOCATION,
  CLOSED_BY_CONTRACTOR,
  CLOSED_BY_COMPETITOR,
  { value: 'CANCELLED_BY_UNIT', title: 'Vaga cancelada pela unidade' },
  OTHER
]

export const emptyChangeReason = (): ChangeReasonValue => ({ code: null, note: '' })

/** Decisões 20 e 41: mais de 30 × 24 h até o início do plantão clicado (estrito). */
export function isEarlyWithdrawalWindow(
  startsAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (!startsAt) return false
  const start = new Date(startsAt).getTime()
  if (Number.isNaN(start)) return false
  return start - now > EARLY_WITHDRAWAL_MIN_MS
}

/** E3: o início do plantão é `startHour ?? date`, como o backend conta (detalhe pode vir sem hora). */
export function stationStartsAt(
  station: { startHour?: string | null; date?: string | null } | null | undefined
): string | null {
  return station?.startHour || station?.date || null
}

/** Decisões 19 e 20: as duas opções condicionais da remoção. */
export function removalReasonOptions(rules: {
  earlyWithdrawal: boolean
  offAppSwap: boolean
}): ChangeReasonOption[] {
  return REMOVAL_REASON_OPTIONS.filter((option) => {
    if (option.value === 'EARLY_WITHDRAWAL') return rules.earlyWithdrawal
    if (option.value === 'OFF_APP_SWAP') return rules.offAppSwap
    return true
  })
}

/** Como o backend conta (varchar do Postgres): pontos de código depois do trim, não UTF-16. */
export const changeReasonNoteLength = (note: string): number => Array.from(note.trim()).length

export function getChangeReasonError(
  value: ChangeReasonValue,
  options: ChangeReasonOption[]
): ChangeReasonError | null {
  const option = options.find((o) => o.value === value.code)
  if (!option) return 'CODE_REQUIRED'
  if (!option.requiresText) return null
  const length = changeReasonNoteLength(value.note)
  if (length === 0) return 'NOTE_REQUIRED'
  if (length < CHANGE_REASON_NOTE_MIN) return 'NOTE_TOO_SHORT'
  if (length > CHANGE_REASON_NOTE_MAX) return 'NOTE_TOO_LONG'
  return null
}

export const isChangeReasonValid = (
  value: ChangeReasonValue,
  options: ChangeReasonOption[]
): boolean => getChangeReasonError(value, options) === null

/** Valor do campo → `ChangeReasonInput` do contrato; `undefined` quando inválido. */
export function toChangeReasonInput(
  value: ChangeReasonValue,
  options: ChangeReasonOption[]
): ChangeReasonInput | undefined {
  if (!value.code || !isChangeReasonValid(value, options)) return undefined
  const option = options.find((o) => o.value === value.code)
  return option?.requiresText ? { code: value.code, note: value.note.trim() } : { code: value.code }
}

/** Decisão 40. Sufixos usados: 'da tabela', 'da série', 'selecionados', 'que serão criados'. */
export function changeReasonScopeNote(count: number, suffix: string): string {
  return count > 1 ? `Este motivo vale para os ${count} plantões ${suffix}.` : ''
}

export const CHANGE_REASON_LABELS = {
  removal: 'Motivo da remoção',
  exit: 'Motivo da saída',
  external: 'Motivo da inclusão de médico externo',
  opening: 'Motivo da abertura',
  deletion: 'Motivo da exclusão'
} as const

const byCount = (count: number, one: string, many: string) => (count > 1 ? many : one)

/** Títulos do mockup; `release` ("Liberar vaga") não tem mockup — copy deste plano. */
export const CHANGE_REASON_TITLES = {
  removal: (count: number) =>
    byCount(
      count,
      'Por que o médico está saindo deste plantão?',
      'Por que o médico está saindo destes plantões?'
    ),
  removalOf: (name: string, count: number) =>
    `Por que ${name} está saindo ${count > 1 ? 'destes plantões' : 'deste plantão'}?`,
  release: (count: number) =>
    byCount(
      count,
      'Por que esta vaga está sendo liberada?',
      'Por que estas vagas estão sendo liberadas?'
    ),
  external: (count: number) =>
    byCount(
      count,
      'Por que este plantão vai para um médico externo?',
      'Por que estes plantões vão para um médico externo?'
    ),
  opening: (count: number) =>
    byCount(
      count,
      'Por que esta vaga está sendo aberta?',
      'Por que estas vagas estão sendo abertas?'
    ),
  deletion: (count: number) =>
    byCount(
      count,
      'Por que este plantão está sendo excluído?',
      'Por que estes plantões estão sendo excluídos?'
    )
}
